import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Box, styled } from "@mui/material";
import { BottomNavigationAction as MuiBottomNavigationAction } from "@material-ui/core";

import AppState from "../store/AppState";
import {
  selectAccountFeatures,
  selectCallerAccountId,
} from "../store/app-config/AppConfigSelectors";
import { setModalState } from "../store/modal/ModalActions";
import {
  selectEditSequenceList,
  selectIsFetchingCategoryProductIds,
  selectNext,
} from "../store/product-list/ProductListSelectors";
import { classicTheme } from "../styles/themes/classic";
import { heatMapModalId } from "./heat-map-modal/HeatMapModal";

/**
 * Heat Map button — rendered as an overlay-tab-style item directly beneath
 * the Analytics overlay tab in OverlayTabs.
 */

/** Wraps the action + the L-line connector. */
const StyledHeatMapWrapper = styled(Box)(({ theme }) => ({
  marginLeft: "30px",
  marginTop: "-3px",
  marginBottom: theme.spacing(1),
  display: "flex",
  justifyContent: "center",
  justifyItems: "flex-start"
}));

/** L-shape connector. */
const StyledAuxiliaryLine = styled(Box)({
  position: "relative",
  width: "10px",
  height: "23px",
  top: "-10px",
  borderLeft: "1px solid #a7a9ac",
  borderBottom: "1px solid #a7a9ac",
});

/**
 * BottomNavigationAction styled to match the other overlay tabs in
 * OverlayTabs.tsx (icon + label, bold 0.875rem, primary-hover color,
 * 0.4 opacity when disabled).
 */
const StyledHeatMapAction = styled(MuiBottomNavigationAction)({
  flex: "inherit",
  minWidth: 0,
  overflow: "hidden",
  fontWeight: "bold",
  margin: 5,
  padding: 0,
  alignItems: "flex-start",
  "& .MuiBottomNavigationAction-wrapper": {
    display: "block",
  },
  "& .MuiBottomNavigationAction-label": {
    fontSize: "0.875rem",
    marginLeft: "8px",
    fontWeight: "bold",
  },
  "& i": {
    color: "inherit",
  },
  "&:hover, &:hover i": {
    color: classicTheme.palette.primaryHoverColor?.main,
  },
  "&.Mui-selected, &.Mui-selected i": {
    color: classicTheme.palette.primary.main,
  },
  "&.Mui-disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
  },
});

const HeatMapButton: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const accountId = useSelector(selectCallerAccountId);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const isHeatMapEnabled = features?.["heat-map"] ?? false;

  // Disable when the current category has no products to visualize.
  const productsInCategory = useSelector(selectEditSequenceList);
  const hasProducts = productsInCategory?.length > 0;

  // Also disable while the catalog's full product-id pagination is still
  // in flight — the heat map needs the complete ordered ID list to batch
  // analytics fetches deterministically.
  const isFetchingProductIds = useSelector(selectIsFetchingCategoryProductIds);
  const hasMoreIdsPending = useSelector(selectNext);
  const allIdsLoaded = !isFetchingProductIds && !hasMoreIdsPending;

  const isInteractive = hasProducts && allIdsLoaded;

  if (!isHeatMapEnabled) return null;

  const handleClick = () => {
    if (!isInteractive) return;
    dispatch(setModalState(heatMapModalId, true));
  };

  return (
    <StyledHeatMapWrapper>
      <StyledAuxiliaryLine />
      <StyledHeatMapAction
        showLabel
        disableRipple
        disabled={!isInteractive}
        icon={<i className="fas fa-th" />}
        label={intl.formatMessage({
          id: "heatMap.label",
          defaultMessage: "Heat Map",
        })}
        onClick={handleClick}
        aria-label="Heat Map"
      />
    </StyledHeatMapWrapper>
  );
};

export default HeatMapButton;
